import React, { useState } from 'react';
import { Printer, Download, Mail, X } from 'lucide-react';
import syncService from '../../services/syncService';
import { useAuth } from '../../contexts/AuthContext';
import Spinner from './Spinner';
import Toast from './Toast';
import './ReportActions.css';

// Shared report actions — Download CSV, Print, Email — used by every Sync Report (Overview, On-site
// instructions, …). The report owns its CONTENT (buildCsv / buildTextBody / buildHtmlBody); this
// component owns the DELIVERY (download, print, recipient picker + SES send). Recipients + email go
// through the generic /report-recipients + /report-email endpoints, so a new report gets email +
// download for free by dropping this in with its builders.
//
// Props:
//   syncId, taskId                        — route ids
//   disabled                                  — disable actions (loading / nothing to send)
//   noun                                      — e.g. "report" / "on-site instructions" (modal copy)
//   defaultSubject, defaultIntro              — pre-fill the email subject + intro note
//   csvFilename                               — attachment + download file name
//   buildCsv()          -> string            — CSV text (download + attachment)
//   buildTextBody()     -> string            — plain-text rendering (email fallback body)
//   buildHtmlBody(intro)-> string            — HTML rendering (email body; `intro` is the operator note)
//   serverReport        -> object|null       — build the email ON THE SERVER instead of posting a body
//
// serverReport, e.g. { report: 'project-onsite', errorsOnly: true }, is passed straight to the
// report-email route, which then renders the body and the attachments itself and ignores the builders.
// A report uses it when the email is not simply the page as text:
//
//   * the on-site instructions run to thousands of record ids. The email carries a heading and a count
//     per extraction task and the ids ride along in an attached PDF — posting the ids up so the server
//     could drop them again would be silly, and building the PDF in the browser would put a second
//     implementation on a page that is also emailed on a SCHEDULE, where there is no browser at all.
//
// The builders stay required regardless: they are still what Download CSV and Print use on screen.

// How many record ids a report shows before the list is folded away — on screen AND in the email.
//
// SHARED so the two cannot drift. The page has collapsed long id lists behind a chevron since it was
// built; the email did not, so a group of 1000 reservations arrived as 1000 rows of numbers with the
// next instruction a screen and a half below it. The ids are for working THROUGH a group, not for
// finding out what the groups are, and that is as true in an inbox as on the page.
export const ID_LIST_OPEN_LIMIT = 20;

/**
 * Fold a long block of email HTML behind a native disclosure triangle.
 *
 * <details>/<summary> AND NOTHING ELSE. An email cannot carry JavaScript and its <style> block is
 * stripped by most clients, so this is the only collapse mechanism available — and it degrades exactly
 * the right way: a client that does not implement <details> (Outlook's Word engine, and Gmail depending
 * on the day) drops the tag and renders the contents, which is the fully-expanded list the email sends
 * today. Nothing is ever hidden from a reader whose client cannot open it.
 *
 * Short lists are returned untouched: a list shorter than its own heading gains nothing from a click.
 *
 * @param count      how many rows the block holds — decides whether to fold at all
 * @param summary    the text on the closed row, e.g. "1000 records"
 * @param innerHtml  the block itself
 */
export function htmlCollapsible(count, summary, innerHtml) {
    if (!innerHtml) return '';
    if (count <= ID_LIST_OPEN_LIMIT) return innerHtml;
    return `<details style="margin:8px 0 0">`
        + `<summary style="cursor:pointer;font-size:12px;color:#6b7280;font-weight:600;padding:2px 0">${summary}</summary>`
        + `<div style="margin:8px 0 0">${innerHtml}</div>`
        + `</details>`;
}

const RECIPIENT_LABELS = { me: 'Me', hotel: 'Hotel', chain: 'Chain', partner: 'Partner', contact: 'Project contact' };
const RECIPIENT_ORDER = ['me', 'hotel', 'chain', 'partner', 'contact'];

export default function ReportActions({
    syncId, taskId, disabled, noun = 'report',
    defaultSubject = '', defaultIntro = '', csvFilename = 'report.csv',
    buildCsv, buildTextBody, buildHtmlBody, serverReport = null, onSent,
}) {
    const { user, isSuperAdmin } = useAuth();
    const [emailOpen, setEmailOpen] = useState(false);
    const [recipients, setRecipients] = useState(null);
    const [recipientKey, setRecipientKey] = useState('');
    const [subject, setSubject] = useState('');
    const [intro, setIntro] = useState('');
    const [sending, setSending] = useState(false);
    // Daily schedule (V446). Only offered for a server-built report, because only those can be produced
    // again with no browser attached.
    const [scheduleDaily, setScheduleDaily] = useState(false);
    const [toast, setToast] = useState(null);
    const [schedules, setSchedules] = useState([]);
    const [sendResult, setSendResult] = useState(null);
    // Ad-hoc addresses, superadmin only. The radio list above is who the platform KNOWS about for this
    // task; this is for the people it does not — a regional manager, a consultant, the property's own
    // distribution list — without adding them to the project's contacts to send one report.
    const [extras, setExtras] = useState([]);
    const [extraInput, setExtraInput] = useState('');
    const [extraError, setExtraError] = useState(null);

    const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

    // Accepts one address or several at once — pasting a comma- or space-separated list is how anyone
    // actually has these to hand, and making them add one at a time is a small cruelty.
    const addExtras = () => {
        const raw = extraInput.split(/[\s,;]+/).map(e => e.trim()).filter(Boolean);
        if (!raw.length) return;
        const bad = raw.filter(e => !EMAIL_RE.test(e));
        if (bad.length) { setExtraError(`Not a valid email address: ${bad.join(', ')}`); return; }
        const known = new Set([...extras, recipients?.[recipientKey]?.email].filter(Boolean));
        setExtras([...extras, ...raw.filter(e => !known.has(e))]);
        setExtraInput(''); setExtraError(null);
    };

    const download = () => {
        const blob = new Blob([buildCsv ? buildCsv() : ''], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = csvFilename; a.click();
        URL.revokeObjectURL(url);
    };

    const openEmail = async () => {
        setEmailOpen(true); setSendResult(null);
        setSubject(defaultSubject);
        setIntro(defaultIntro);
        // RECIPIENTS ARE FETCHED ONCE, SCHEDULES EVERY TIME.
        //
        // The address book does not change while the dialog is closed. A schedule does — it is created
        // by the send this very dialog just made — so caching it with the rest meant setting one up and
        // then being offered the checkbox again on the next open, with no sign the first had worked.
        let fresh = null;
        try { fresh = (await syncService.getReportRecipients(syncId, taskId)) || {}; } catch (e) { fresh = {}; }
        setSchedules(Array.isArray(fresh.schedules) ? fresh.schedules : []);

        if (!recipients) {
            const r = fresh;
            if (user?.email) r.me = { name: user.name || user.given_name || user.email, contact: user.name || '', email: user.email };
            setRecipients(r);
            // Prefill the ad-hoc addresses this project's last report went to (V445), so the same
            // people do not have to be typed again on every report and every task. They are chips like
            // any other — removing one and sending is what forgets it.
            if (Array.isArray(r.saved) && r.saved.length) setExtras(r.saved);
            const first = RECIPIENT_ORDER.find(k => r[k] && r[k].email);
            if (first) setRecipientKey(first);
        }
    };

    const send = async () => {
        const rec = recipients?.[recipientKey];
        // A selected recipient OR at least one extra address. Requiring the radio would mean a
        // superadmin sending only to an outside address has to pick somebody to also send it to.
        const all = [...new Set([rec?.email, ...extras].filter(Boolean))];
        if (!all.length) return;
        try {
            setSending(true); setSendResult(null);
            const textBody = (intro ? intro + '\n\n' : '') + (buildTextBody ? buildTextBody() : '');
            const payload = {
                toEmail: rec?.email || '', toName: rec?.contact || rec?.name || '',
                toEmails: all, subject,
            };
            if (serverReport) {
                // The server renders body, HTML and attachments. `intro` still comes from here — it is
                // the operator's own note and belongs to this send, not to the report.
                Object.assign(payload, serverReport, { intro, scheduleDaily });
            } else {
                Object.assign(payload, {
                    body: textBody, bodyHtml: buildHtmlBody ? buildHtmlBody(intro) : undefined,
                    csv: buildCsv ? buildCsv() : '', csvFilename,
                });
            }
            const res = await syncService.sendReportEmail(syncId, taskId, payload);
            const sentMsg = all.length === 1 ? `Email sent to ${all[0]}` : `Email sent to ${all.length} recipients`;
            // The schedule is reported alongside the send rather than instead of it: the email HAS
            // gone, and a schedule that failed to save must not read as a failed send.
            const sched = res && res.schedule;

            // THE DIALOG CLOSES ON SUCCESS and the confirmation moves to a toast.
            //
            // Leaving it open put a green line under a form still showing a Send button and an unticked
            // checkbox — inviting the reader to wonder whether it had gone, or to press Send again. The
            // work is finished, so the form has nothing left to do; the toast says so over the page
            // they are returning to.
            //
            // A FAILED schedule keeps the dialog open: that one is not finished, and its message is
            // something to act on rather than to watch disappear.
            if (sched && sched.error) {
                setSendResult({ ok: true, msg: `${sentMsg} — but the daily schedule could not be saved: ${sched.error}` });
            } else {
                setToast({
                    kind: 'success',
                    message: sched && sched.endsOn
                        ? `${sentMsg} and scheduled daily until ${sched.endsOn}.`
                        : `${sentMsg}.`,
                });
                setScheduleDaily(false);
                setEmailOpen(false);
                setSendResult(null);
                // The page owns anything that outlives this dialog — the schedule strip, in particular,
                // which has just gained a row the page cannot know about.
                if (onSent) onSent({ recipients: all, schedule: sched || null });
            }
        } catch (e) {
            setSendResult({ ok: false, msg: e.response?.data?.message || e.message || 'Send failed' });
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            {/* Outside the modal on purpose — the modal is gone by the time this is read. */}
            <Toast {...(toast || {})} onClose={() => setToast(null)} />

            <div className="ra-actions">
                <button className="ra-btn" onClick={download} disabled={disabled}><Download size={15} /> Download CSV</button>
                <button className="ra-btn" onClick={() => window.print()} disabled={disabled}><Printer size={15} /> Print</button>
                <button className="ra-btn ra-btn-primary" onClick={openEmail} disabled={disabled}><Mail size={15} /> Email</button>
            </div>

            {emailOpen && (
                <div className="ra-modal-overlay" onClick={() => !sending && setEmailOpen(false)}>
                    <div className="ra-modal" onClick={e => e.stopPropagation()}>
                        <div className="ra-modal-head">
                            <h3>Email {noun}</h3>
                            <button className="ra-modal-close" onClick={() => !sending && setEmailOpen(false)}><X size={18} /></button>
                        </div>
                        <div className="ra-modal-body">
                            <label className="ra-field-label">Send to</label>
                            <div className="ra-recipients">
                                {recipients === null ? <Spinner /> : RECIPIENT_ORDER.map(k => {
                                    const rec = recipients[k];
                                    const off = !rec || !rec.email;
                                    return (
                                        <label key={k} className={`ra-recipient ${off ? 'ra-recipient-disabled' : ''} ${recipientKey === k ? 'ra-recipient-sel' : ''}`}>
                                            <input type="radio" name="ra-recipient" disabled={off} checked={recipientKey === k} onChange={() => setRecipientKey(k)} />
                                            <span className="ra-recipient-role">{RECIPIENT_LABELS[k]}</span>
                                            <span className="ra-recipient-email">{rec && rec.email ? rec.email : '— no email on file —'}</span>
                                        </label>
                                    );
                                })}
                            </div>
                            {/* Superadmin only — see the server, which rejects `toEmails` from anyone
                                else rather than trusting this to be hidden. */}
                            {isSuperAdmin && (
                                <div className="ra-extra sa-only-panel">
                                    <label className="ra-field-label">Also send to</label>
                                    <div className="ra-extra-add">
                                        <input
                                            className="ra-input"
                                            value={extraInput}
                                            placeholder="name@example.com — several at once is fine"
                                            onChange={e => { setExtraInput(e.target.value); setExtraError(null); }}
                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addExtras(); } }}
                                            onBlur={addExtras}
                                        />
                                        <button type="button" className="ra-btn" onClick={addExtras} disabled={!extraInput.trim()}>Add</button>
                                    </div>
                                    {extraError && <p className="ra-send-err">{extraError}</p>}
                                    {extras.length > 0 && (
                                        <div className="ra-chips">
                                            {extras.map(e => (
                                                <span key={e} className="ra-chip">
                                                    {e}
                                                    <button type="button" className="ra-chip-x" title="Remove"
                                                        onClick={() => setExtras(extras.filter(x => x !== e))}>
                                                        <X size={12} />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <p className="ra-attach-note">
                                        Everyone is on one message and can see the other addresses.
                                        This list is remembered for the next report on this project —
                                        remove an address and send to forget it.
                                    </p>
                                </div>
                            )}
                            <label className="ra-field-label">Subject</label>
                            <input className="ra-input" value={subject} onChange={e => setSubject(e.target.value)} />
                            <label className="ra-field-label">Message (optional)</label>
                            <textarea className="ra-textarea" rows={4} value={intro} onChange={e => setIntro(e.target.value)} />
                            {serverReport && serverReport.allowEmpty ? (
                                <p className="ra-attach-note">
                                    There is nothing outstanding, so this sends a short all-clear —
                                    no attachment.
                                </p>
                            ) : serverReport ? (
                                <p className="ra-attach-note">
                                    The message lists each section and how many records it covers. The
                                    record ids and the full instructions are attached as a PDF — use
                                    Download CSV above if you want the same detail in a spreadsheet.
                                </p>
                            ) : (
                                <p className="ra-attach-note">The full {noun} is rendered (formatted) below your message. Attaches <code>{csvFilename}</code>.</p>
                            )}
                            {/* ── DAILY SCHEDULE (V446) ──
                                Superadmin-only, like the extra addresses, and only for a server-built
                                report — a scheduled run has no browser, so only those can be produced
                                again identically.

                                Created by SENDING, not by ticking alone: the first one goes out now and
                                you see it. Setting up a daily email to a property without having seen
                                one is how something nobody checked arrives every morning for a week. */}
                            {serverReport && isSuperAdmin && (
                                <div className="ra-schedule sa-only-panel">
                                    {schedules.length > 0 ? (
                                        <div className="ra-schedule-active">
                                            <strong>Already sending daily</strong>
                                            {schedules.map(s => (
                                                <div key={s.id} className="ra-schedule-row">
                                                    until {s.endsOn ? String(s.endsOn).slice(0, 10) : '—'}
                                                    {' · '}{(s.recipients || []).length} recipient{(s.recipients || []).length === 1 ? '' : 's'}
                                                    {s.sendCount > 0 && ` · sent ${s.sendCount}×`}
                                                    {s.lastError && <span className="ra-schedule-err"> · last run failed: {s.lastError}</span>}
                                                </div>
                                            ))}
                                            <p className="ra-attach-note">
                                                Sending now also updates that schedule to these recipients, subject and message.
                                                Stop it from the report page.
                                            </p>
                                        </div>
                                    ) : (
                                        <label className="ra-schedule-toggle">
                                            <input
                                                type="checkbox"
                                                checked={scheduleDaily}
                                                onChange={e => setScheduleDaily(e.target.checked)}
                                            />
                                            <span>
                                                Send this again every morning until the project's cutover date + 1 day
                                                <span className="ra-schedule-hint">
                                                    {' '}— the exact end date is shown once it is set up, and a day with
                                                    nothing outstanding is skipped rather than mailed.
                                                </span>
                                            </span>
                                        </label>
                                    )}
                                </div>
                            )}
                            {sendResult && <p className={sendResult.ok ? 'ra-send-ok' : 'ra-send-err'}>{sendResult.msg}</p>}
                        </div>
                        <div className="ra-modal-foot">
                            <button className="ra-btn" onClick={() => setEmailOpen(false)} disabled={sending}>Cancel</button>
                            <button className="ra-btn ra-btn-primary" onClick={send}
                                disabled={sending || (!recipients?.[recipientKey]?.email && extras.length === 0)}>
                                {sending ? 'Sending…' : 'Send'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
